import React from "react";
import {
  Bell,
  BookOpenCheck,
  ChevronLeft,
  CircleDollarSign,
  FileCheck2,
  Search,
  ShieldAlert,
  Truck,
} from "lucide-react";

const COPY = {
  ru: {
    title: "Как работать экспедитору",
    settingsTitle: "Инструкция",
    shortDescription: "Поиск груза и машины, проверка, переговоры, маржа и контроль рейса.",
    subtitle: "Рабочая схема REISOR. Открыл, сделал по шагам, закрыл рейс.",
    back: "Назад в настройки",
    goldenTitle: "Ленту листать не нужно",
    goldenText: "Рабочий поиск делайте через REISOR AI или фильтр. Нужное направление сохраните и включите уведомления. Ваша задача — реагировать на подходящее совпадение, а не весь день обновлять ленту.",
    notifyTitle: "Сначала включите уведомления",
    notifyText: "Без уведомлений сохранённый поиск теряет смысл: новый подходящий груз может появиться, пока приложение закрыто.",
    notifyButton: "Включить уведомления",
    notifyBusy: "Подключаем…",
    notifyDone: "Уведомления включены",
    flowTitle: "Рабочий цикл в 6 шагах",
    flow: [
      "Выберите, что у вас уже есть: свободная машина или подтверждённый груз.",
      "Найдите вторую сторону через AI, точный фильтр, «Машины» или «Мой парк».",
      "До обещаний перепроверьте актуальность и все ключевые условия.",
      "Проверьте водителя, машину, перевозчика и нужные для этого рейса документы.",
      "Зафиксируйте отдельно цену клиента и цену перевозчика, посчитайте маржу до выезда.",
      "Контролируйте подачу, загрузку, путь, выгрузку, документы и оплату до полного закрытия.",
    ],
    sections: [
      {
        id: "search",
        icon: Search,
        title: "1. Поиск: только AI, фильтр и уведомления",
        bullets: [
          "Грузы: нажмите «Поиск заказов» и задайте Откуда, Куда и кузов. Не ищите нужный рейс прокруткой общей ленты.",
          "Если проще написать словами, используйте REISOR AI: маршрут, кузов, тоннаж и когда машина готова.",
          "Начинайте со свежих грузов. Если совпадений нет, AI/поиск может расширять окно до 6, 12 и 24 часов. Чем старше объявление, тем важнее повторная проверка звонком.",
          "Подходящий маршрут сохраните через «Сохранить и уведомлять». Для рабочих направлений уведомления держите включёнными.",
          "Машины: откройте «Машины» и фильтруйте по месту, направлению, кузову и догрузу. Свой постоянный транспорт держите в «Мой парк».",
        ],
      },
      {
        id: "vehicle-to-load",
        icon: Truck,
        title: "2. Есть свободная машина → найдите груз",
        bullets: [
          "Сначала зафиксируйте по машине 5 вещей: где она сейчас, когда свободна, куда готова ехать, кузов, грузоподъёмность/объём.",
          "По этим данным запустите AI или точный поиск груза. Смотрите прежде всего совпадение по маршруту и машине, потом ставку.",
          "Нашли груз — не обещайте машину сразу. Позвоните по грузу и подтвердите, что он ещё свободен и условия не изменились.",
          "После подтверждения груза снова подтвердите готовность водителя: место, время подачи и его окончательную цену.",
          "Только когда обе стороны подтверждены, фиксируйте сделку и переходите к контролю подачи.",
        ],
      },
      {
        id: "load-to-vehicle",
        icon: Truck,
        title: "3. Есть груз → найдите машину",
        bullets: [
          "До поиска машины уточните груз: точный маршрут, дата/время, наименование, вес/объём, кузов, загрузка/выгрузка, ставка и оплата.",
          "Откройте «Машины». Сначала ищите максимально точное совпадение по месту машины, направлению, кузову и вместимости.",
          "Проверьте «Мой парк», если у вас есть свои или постоянные машины. Не звоните всем подряд: сначала самые подходящие кандидаты.",
          "До подтверждения клиенту водитель должен подтвердить: машина реально свободна, подходит под груз, успевает на подачу и согласен на окончательную цену.",
          "Если груз старый или машина давно опубликована, актуальность проверяется повторно непосредственно перед фиксацией.",
        ],
      },
      {
        id: "call-check",
        icon: BookOpenCheck,
        title: "4. Один звонок: что обязательно уточнить",
        note: "Не начинайте торг, пока не понятны факты. Сначала условия рейса, потом цена.",
        bullets: [
          "Груз актуален / машина свободна прямо сейчас?",
          "Откуда и куда: город, при необходимости точный адрес, дата и окно подачи.",
          "Что за груз, вес, объём, количество мест; какой кузов и какая загрузка/выгрузка нужны.",
          "Есть ли особые условия: температура, догруз, верхняя/боковая загрузка, режим, граница, таможня, возврат тары и т.п.",
          "Окончательная ставка, валюта, с НДС или без, кто кому платит, аванс, срок остатка, условия простоя.",
          "Контакт ответственного на погрузке/выгрузке и кто принимает решение по ставке.",
          "После разговора отправьте короткое письменное подтверждение условий. Если что-то изменилось — старое подтверждение больше не используйте.",
        ],
      },
      {
        id: "documents",
        icon: FileCheck2,
        title: "5. Документы: проверяйте только то, что нужно рейсу",
        note: "Не превращайте проверку в экзамен. Цель — убедиться, что водитель, машина, перевозчик и груз могут выполнить именно этот рейс.",
        bullets: [
          "Водитель: документ личности/паспорт, водительское удостоверение нужной категории, рабочий телефон.",
          "Машина: регистрационные документы тягача/автомобиля и прицепа, номера, действующая страховка и соответствие заявленному кузову/тоннажу.",
          "Перевозчик: название/ФИО, рабочие реквизиты и контакт. Данные должны совпадать с тем, с кем вы договариваетесь и кому будете платить.",
          "Внутренний рейс: товарные/транспортные документы, которые даёт грузоотправитель для конкретной перевозки.",
          "Международный рейс: паспорт/въездные документы при необходимости, CMR и разрешение/дозвол, если оно требуется для этого маршрута.",
          "TIR проверяется только если рейс действительно идёт по TIR. ADR — только для опасного груза. Специальные документы — только когда их требует конкретный товар или маршрут.",
          "Если документ вызывает сомнение, не спорьте и не рискуйте деньгами: поставьте сделку на паузу и перепроверьте до подачи машины.",
        ],
      },
      {
        id: "margin",
        icon: CircleDollarSign,
        title: "6. Переговоры и маржа: фиксируйте до выезда",
        note: "Маржа не появляется в конце рейса. Она должна быть понятна до того, как машина поехала на погрузку.",
        bullets: [
          "С клиентом фиксируйте его конечную цену и условия оплаты. С перевозчиком отдельно фиксируйте сумму, которую получите/заплатите ему по договорённости.",
          "Не пересылайте клиенту переписку или скрин с ценой перевозчика и наоборот. Два переговорных контура должны оставаться отдельными.",
          "Маржа = цена клиента − цена перевозчика − прямые расходы по этой сделке.",
          "До фиксации проверьте, кто оплачивает простой, перегруз, дополнительные точки, платные дороги/сборы и другие возможные расходы. Иначе они съедят маржу.",
          "Не подтверждайте рейс, если неясно: кто платит, кому платит, когда платит, в какой валюте и на какие реквизиты.",
          "После согласования отправьте каждой стороне её условия одним коротким сообщением и получите подтверждение. Не меняйте согласованные условия задним числом ради большей маржи.",
        ],
      },
      {
        id: "trip",
        icon: BookOpenCheck,
        title: "7. Рейс: контроль от подачи до оплаты",
        bullets: [
          "До подачи: водитель, телефон, госномер, время выезда, ETA и контакт погрузки подтверждены.",
          "На погрузке: машина прибыла; груз, вес и условия совпадают; водитель получил необходимые документы.",
          "В пути: контролируйте ключевые точки и задержки. Если возникла проблема, сначала получите факт от водителя, затем сразу сообщите клиенту с новым ETA/решением.",
          "На выгрузке: получите подтверждение доставки и подписанные документы/подтверждение приёмки, которые нужны по этому рейсу.",
          "После выгрузки: закройте расчёты, проверьте фактические расходы и фактическую маржу. Рейс считается закрытым, когда нет незакрытого документа, спора или платежа.",
        ],
      },
      {
        id: "stop",
        icon: ShieldAlert,
        title: "8. Когда нужно остановиться и перепроверить",
        bullets: [
          "Цена, маршрут, вес, кузов или дата внезапно поменялись после договорённости.",
          "Перед оплатой поменялись получатель, карта, счёт или реквизиты.",
          "Человек не может подтвердить компанию, машину, водителя или контакт на погрузке.",
          "Требуют срочный аванс до проверки основных данных.",
          "Машина якобы свободна, но водитель не подтверждает место или время подачи.",
          "Груз якобы актуален, но никто не подтверждает погрузку.",
          "В любой такой ситуации не спорьте и не угадывайте. Статус сделки: ПАУЗА → повторная проверка → только потом продолжение.",
        ],
      },
      {
        id: "terms",
        icon: BookOpenCheck,
        title: "9. Только нужные термины",
        bullets: [
          "Ставка — согласованная цена перевозки.",
          "Маржа — то, что остаётся между ценой клиента и стоимостью перевозчика после прямых расходов.",
          "Перевозчик — сторона, которая фактически выполняет перевозку машиной/водителем.",
          "Экспедитор — организует перевозку: находит стороны, согласует условия и контролирует исполнение.",
          "Догруз / partial — груз занимает только часть машины; в кузове могут быть другие совместимые грузы.",
          "CMR — международная автотранспортная накладная для соответствующего международного рейса.",
          "Дозвол — разрешение на международный маршрут, когда оно требуется.",
          "TIR — режим международного таможенного транзита; используется не в каждом рейсе.",
          "ADR — требования к перевозке опасных грузов.",
          "Простой — ожидание сверх согласованных условий на погрузке/выгрузке.",
          "ETA — ожидаемое время прибытия.",
          "POD — подтверждение доставки/приёмки груза.",
        ],
      },
    ],
    finishTitle: "Главная формула",
    finishText: "Не листать → задать точный поиск → включить уведомления → подтвердить актуальность → проверить машину и документы → зафиксировать две цены → сохранить маржу → контролировать рейс до денег и документов.",
  },

  uz: {
    title: "Экспедитор қандай ишлаши керак",
    settingsTitle: "Йўриқнома",
    shortDescription: "Юк ва машина қидириш, текшириш, музокара, маржа ва рейс назорати.",
    subtitle: "REISOR иш схемаси. Очинг, қадамма-қадам бажаринг, рейсни ёпинг.",
    back: "Созламаларга қайтиш",
    goldenTitle: "Лентани варақлаш шарт эмас",
    goldenText: "Иш қидирувини REISOR AI ёки фильтр орқали қилинг. Керакли йўналишни сақланг ва билдиришномани ёқинг. Сизнинг вазифангиз — мос вариант келганда тез ишлаш, кун бўйи лентани янгилаш эмас.",
    notifyTitle: "Аввал билдиришномани ёқинг",
    notifyText: "Билдиришномасиз сақланган қидирувнинг фойдаси камаяди: янги мос юк илова ёпиқ пайтда ҳам чиқиши мумкин.",
    notifyButton: "Билдиришномани ёқиш",
    notifyBusy: "Улаяпмиз…",
    notifyDone: "Билдиришнома ёқилган",
    flowTitle: "6 қадамлик иш цикли",
    flow: [
      "Қўлингизда нима борлигини аниқланг: бўш машина ёки тасдиқланган юк.",
      "Иккинчи томонни AI, аниқ фильтр, «Машиналар» ёки «Менинг парким» орқали топинг.",
      "Ваъда беришдан олдин актуаллик ва асосий шартларни қайта тасдиқланг.",
      "Ҳайдовчи, машина, ташувчи ва шу рейс учун керакли ҳужжатларни текширинг.",
      "Мижоз нархи ва ташувчи нархини алоҳида белгиланг, машина чиқишидан олдин маржани ҳисобланг.",
      "Машина бериш, юклаш, йўл, тушириш, ҳужжат ва тўловни тўлиқ ёпилгунча назорат қилинг.",
    ],
    sections: [
      {
        id: "search",
        icon: Search,
        title: "1. Қидирув: фақат AI, фильтр ва билдиришнома",
        bullets: [
          "Юклар: «Буюртма қидириш»ни очинг ва Қаердан, Қаерга, кузовни киритинг. Керакли рейсни умумий лентани варақлаб қидирманг.",
          "Сўз билан ёзиш осон бўлса, REISOR AI'га йўналиш, кузов, тоннаж ва машина қачон тайёрлигини ёзинг.",
          "Аввал янги юкларни қидиринг. Мос юк бўлмаса AI/қидирув ойнасини 6, 12 ва 24 соатгача кенгайтириши мумкин. Эскироқ эълонни телефонда қайта тасдиқлаш шарт.",
          "Фойдали йўналишни «Сақлаш ва хабар бериш» орқали сақланг. Ишлайдиган йўналишларда билдиришномани ёқиб қўйинг.",
          "Машиналар: «Машиналар» бўлимида жойи, йўналиши, кузови ва догруз бўйича фильтрланг. Доимий машиналарни «Менинг парким»да сақланг.",
        ],
      },
      {
        id: "vehicle-to-load",
        icon: Truck,
        title: "2. Бўш машина бор → унга юк топинг",
        bullets: [
          "Аввал машина бўйича 5 маълумотни аниқланг: ҳозир қаерда, қачон бўш, қаерга боришга тайёр, кузови, юк кўтариши/ҳажми.",
          "Шу маълумотлар билан AI ёки аниқ юк қидирувини ишга туширинг. Аввал йўналиш ва машина мослиги, кейин ставка.",
          "Юк топилса, машинани дарров ваъда қилманг. Юк эгаси билан юк ҳали актуал эканини ва шартлар ўзгармаганини тасдиқланг.",
          "Юк тасдиқлангандан кейин ҳайдовчидан яна бир марта жойи, машина бериш вақти ва охирги нархини тасдиқланг.",
          "Икки томон ҳам тасдиқлангандан кейингина келишувни белгилаб, машина бериш назоратига ўтинг.",
        ],
      },
      {
        id: "load-to-vehicle",
        icon: Truck,
        title: "3. Юк бор → машина топинг",
        bullets: [
          "Машина қидиришдан олдин юкни аниқланг: йўналиш, сана/вақт, юк номи, оғирлик/ҳажм, кузов, юклаш/тушириш, ставка ва тўлов.",
          "«Машиналар»ни очинг. Аввал машина жойи, йўналиш, кузов ва сиғим бўйича энг аниқ мос келадиганларини кўринг.",
          "Ўз ёки доимий машиналарингиз бўлса «Менинг парким»ни текширинг. Ҳаммага қўнғироқ қилманг — аввал энг мос номзодлар.",
          "Мижозга тасдиқ беришдан олдин ҳайдовчи: машина ҳақиқатан бўш, юкка мос, вақтда етади ва охирги нархга рози эканини тасдиқлаши керак.",
          "Юк ёки машина эълони эски бўлса, келишувни белгилашдан олдин актуаллик қайта текширилади.",
        ],
      },
      {
        id: "call-check",
        icon: BookOpenCheck,
        title: "4. Битта қўнғироқда нимани аниқлаш керак",
        note: "Фактлар аниқ бўлмасдан савдолашманг. Аввал рейс шарти, кейин нарх.",
        bullets: [
          "Юк ҳозир ҳам актуалми / машина ҳозир ҳам бўшми?",
          "Қаердан ва қаерга: шаҳар, керак бўлса аниқ манзил, сана ва машина бериш оралиғи.",
          "Қандай юк, оғирлик, ҳажм, жойлар сони; қандай кузов ва қандай юклаш/тушириш керак.",
          "Махсус шарт борми: температура, догруз, юқоридан/ёндан юклаш, режим, чегара, божхона, тара қайтариш ва ҳ.к.",
          "Охирги ставка, валюта, ҚҚС билан/ҚҚСсиз, ким кимга тўлайди, аванс, қолдиқ тўлов муддати, простой шарти.",
          "Юклаш/туширишдаги масъул контакт ва ставкани ким тасдиқлаши.",
          "Суҳбатдан кейин шартларни қисқа ёзма хабарда жўнатинг. Бирор шарт ўзгарса, эски тасдиққа таянманг.",
        ],
      },
      {
        id: "documents",
        icon: FileCheck2,
        title: "5. Ҳужжатлар: фақат шу рейсга кераклисини текширинг",
        note: "Мақсад ҳужжатлардан имтиҳон олиш эмас. Ҳайдовчи, машина, ташувчи ва юк айнан шу рейсни қонуний ва амалий бажара олишини текшириш.",
        bullets: [
          "Ҳайдовчи: шахсни тасдиқловчи ҳужжат/паспорт, керакли тоифадаги ҳайдовчилик гувоҳномаси, ишлайдиган телефон.",
          "Машина: автомобиль/тягач ва тиркама рўйхат ҳужжатлари, давлат рақамлари, амал қилувчи суғурта ва айтилган кузов/тоннажга мослиги.",
          "Ташувчи: номи/Ф.И.Ш., ишлайдиган реквизитлар ва контакт. Маълумот сиз келишган ва пул тўлайдиган томон билан мос бўлиши керак.",
          "Ички рейс: конкрет ташиш учун юк жўнатувчи берадиган товар ва транспорт ҳужжатлари.",
          "Халқаро рейс: керак бўлса паспорт/кириш ҳужжатлари, CMR ва шу йўналиш талаб қилса рухсат/дозвол.",
          "TIR фақат рейс ҳақиқатан TIR бўйича кетса текширилади. ADR — фақат хавфли юкда. Махсус ҳужжат — фақат товар ёки йўналиш талаб қилса.",
          "Ҳужжат шубҳали бўлса тортишманг ва пулни хавфга қўйманг: машина чиқишидан олдин келишувни ПАУЗАга қўйиб қайта текширинг.",
        ],
      },
      {
        id: "margin",
        icon: CircleDollarSign,
        title: "6. Музокара ва маржа: машина чиқишидан олдин белгиланг",
        note: "Маржа рейс охирида тасодифан чиқмайди. У машина юклашга чиқишидан олдин аниқ бўлиши керак.",
        bullets: [
          "Мижоз билан унинг якуний нархи ва тўлов шартларини белгиланг. Ташувчи билан унга тўланадиган/у оладиган суммани алоҳида белгиланг.",
          "Мижозга ташувчи нархи кўринган чат ёки скринни, ташувчига эса мижоз нархини жўнатманг. Икки музокара алоҳида бўлиши керак.",
          "Маржа = мижоз нархи − ташувчи нархи − шу битимнинг тўғридан-тўғри харажатлари.",
          "Келишувдан олдин простой, қўшимча нуқта, перегруз, пуллик йўл/йиғим ва бошқа харажатни ким тўлашини аниқланг. Акс ҳолда улар маржани еб қўяди.",
          "Ким тўлайди, кимга тўлайди, қачон, қайси валютада ва қайси реквизитга — тушунарсиз бўлса рейсни тасдиқламанг.",
          "Келишувдан кейин ҳар бир томонга фақат унинг шартларини битта қисқа хабарда ёзиб тасдиқ олинг. Кўпроқ маржа учун келишилган шартни кейин ўзгартирманг.",
        ],
      },
      {
        id: "trip",
        icon: BookOpenCheck,
        title: "7. Рейс: машина беришдан тўловгача назорат",
        bullets: [
          "Машина беришдан олдин: ҳайдовчи, телефон, давлат рақами, чиқиш вақти, ETA ва юклаш контакти тасдиқланган.",
          "Юклашда: машина келди; юк, оғирлик ва шартлар мос; ҳайдовчи керакли ҳужжатларни олди.",
          "Йўлда: асосий нуқта ва кечикишларни назорат қилинг. Муаммо бўлса аввал ҳайдовчидан фактни олинг, кейин мижозга янги ETA/ечим билан хабар беринг.",
          "Туширишда: етказилганини ва шу рейс учун керакли имзоланган ҳужжат/қабул тасдиғини олинг.",
          "Туширишдан кейин: ҳисоб-китобни ёпинг, ҳақиқий харажат ва ҳақиқий маржани текширинг. Очиқ ҳужжат, низо ёки тўлов қолмаса рейс ёпилган ҳисобланади.",
        ],
      },
      {
        id: "stop",
        icon: ShieldAlert,
        title: "8. Қачон тўхтаб қайта текшириш керак",
        bullets: [
          "Келишувдан кейин нарх, йўналиш, оғирлик, кузов ёки сана тўсатдан ўзгарди.",
          "Тўлов олдидан қабул қилувчи, карта, ҳисоб ёки реквизит ўзгарди.",
          "Одам компания, машина, ҳайдовчи ёки юклаш контактини тасдиқлай олмаяпти.",
          "Асосий маълумот текширилмасидан олдин шошилинч аванс сўралаяпти.",
          "Машина бўш дейишяпти, лекин ҳайдовчи жойи ёки машина бериш вақтини тасдиқламайди.",
          "Юк актуал дейишяпти, лекин юклашни ҳеч ким тасдиқламайди.",
          "Бундай ҳолатда тортишманг ва тахмин қилманг. Статус: ПАУЗА → қайта текшириш → кейин давом.",
        ],
      },
      {
        id: "terms",
        icon: BookOpenCheck,
        title: "9. Фақат керакли терминлар",
        bullets: [
          "Ставка — ташиш учун келишилган нарх.",
          "Маржа — мижоз нархи билан ташувчи харажати ўртасида тўғридан-тўғри харажатлардан кейин қоладиган сумма.",
          "Ташувчи — юкни машина/ҳайдовчи билан амалда олиб борадиган томон.",
          "Экспедитор — ташишни ташкил қилади: томонларни топади, шартларни келишади ва бажарилишини назорат қилади.",
          "Догруз / partial — юк машина жойининг бир қисмини эгаллайди; кузовда бошқа мос юклар ҳам бўлиши мумкин.",
          "CMR — тегишли халқаро автомобиль рейси учун халқаро транспорт накладнойи.",
          "Дозвол — халқаро йўналиш учун талаб қилинса бериладиган рухсат.",
          "TIR — халқаро божхона транзити режими; ҳар бир рейсда қўлланмайди.",
          "ADR — хавфли юк ташиш талаблари.",
          "Простой — юклаш/туширишда келишилган шартдан ортиқ кутиш.",
          "ETA — тахминий етиб келиш вақти.",
          "POD — юк етказилгани/қабул қилинганини тасдиқлаш.",
        ],
      },
    ],
    finishTitle: "Асосий формула",
    finishText: "Лентани варақламаслик → аниқ қидирув → билдиришнома → актуалликни тасдиқлаш → машина ва ҳужжатни текшириш → икки нархни белгилаш → маржани сақлаш → рейсни пул ва ҳужжатгача назорат қилиш.",
  },

  tr: {
    title: "Nakliye organizatörü nasıl çalışmalı",
    settingsTitle: "Kılavuz",
    shortDescription: "Yük ve araç arama, doğrulama, pazarlık, marj ve sefer kontrolü.",
    subtitle: "REISOR çalışma akışı. Açın, adımları uygulayın, seferi kapatın.",
    back: "Ayarlara dön",
    goldenTitle: "Akışı kaydırmanız gerekmez",
    goldenText: "Çalışma aramasını REISOR AI veya filtre ile yapın. Gerekli hattı kaydedin ve bildirimleri açın. Göreviniz uygun eşleşme geldiğinde hızlı hareket etmektir; bütün gün akışı yenilemek değildir.",
    notifyTitle: "Önce bildirimleri açın",
    notifyText: "Bildirim olmadan kayıtlı arama verimsiz kalır: uygun yeni yük uygulama kapalıyken de gelebilir.",
    notifyButton: "Bildirimleri aç",
    notifyBusy: "Bağlanıyor…",
    notifyDone: "Bildirimler açık",
    flowTitle: "6 adımlık çalışma döngüsü",
    flow: [
      "Elinizde ne olduğunu belirleyin: boş araç mı, teyit edilmiş yük mü?",
      "Diğer tarafı AI, net filtre, «Araçlar» veya «Filom» üzerinden bulun.",
      "Söz vermeden önce güncelliği ve ana şartları tekrar teyit edin.",
      "Sürücü, araç, taşıyıcı ve bu sefere gereken belgeleri kontrol edin.",
      "Müşteri fiyatını ve taşıyıcı fiyatını ayrı kaydedin; araç çıkmadan marjı hesaplayın.",
      "Araç sevki, yükleme, yol, boşaltma, belgeler ve ödemeyi tamamen kapanana kadar takip edin.",
    ],
    sections: [
      {
        id: "search",
        icon: Search,
        title: "1. Arama: yalnızca AI, filtre ve bildirim",
        bullets: [
          "Yükler: «Sipariş ara» bölümünü açın; Nereden, Nereye ve kasa tipini girin. Uygun işi genel akışı kaydırarak aramayın.",
          "Yazarak anlatmak daha kolaysa REISOR AI'ya rota, kasa, tonaj ve aracın ne zaman hazır olduğunu yazın.",
          "Önce en yeni yükleri arayın. Eşleşme yoksa AI/arama penceresi 6, 12 ve 24 saate kadar genişleyebilir. İlan yaşlandıkça telefonla yeniden teyit daha önemlidir.",
          "İyi hattı «Kaydet ve bildir» ile saklayın. Aktif çalıştığınız hatlarda bildirimleri açık tutun.",
          "Araçlar: «Araçlar» bölümünde konum, yön, kasa ve parsiyel durumuna göre filtreleyin. Sürekli kullandığınız araçları «Filom»da tutun.",
        ],
      },
      {
        id: "vehicle-to-load",
        icon: Truck,
        title: "2. Boş araç var → araca yük bulun",
        bullets: [
          "Önce araç için 5 bilgiyi netleştirin: şu an nerede, ne zaman boş, hangi yöne gider, kasa tipi, kapasite/hacim.",
          "Bu bilgilerle AI veya net yük aramasını başlatın. Önce rota ve araç uyumu, sonra fiyat.",
          "Yük bulunca aracı hemen söz vermeyin. Yük tarafını arayıp yükün hâlâ açık olduğunu ve şartların değişmediğini teyit edin.",
          "Yük teyidinden sonra sürücüden konumunu, yüklemeye yetişme saatini ve son fiyatını tekrar teyit edin.",
          "İki taraf da teyit edilince işi sabitleyin ve araç sevk kontrolüne geçin.",
        ],
      },
      {
        id: "load-to-vehicle",
        icon: Truck,
        title: "3. Yük var → araç bulun",
        bullets: [
          "Araç aramadan önce yükü netleştirin: rota, tarih/saat, yük adı, ağırlık/hacim, kasa, yükleme/boşaltma, fiyat ve ödeme.",
          "«Araçlar»ı açın. Önce aracın konumu, yönü, kasası ve kapasitesi en iyi uyan adaylara bakın.",
          "Kendi veya sürekli araçlarınız varsa «Filom»u kontrol edin. Herkesi aramayın; önce en uygun adaylar.",
          "Müşteriye teyit vermeden önce sürücü: aracın gerçekten boş olduğunu, yüke uygun olduğunu, zamanında yetişeceğini ve son fiyata hazır olduğunu teyit etmelidir.",
          "Yük veya araç ilanı eskiyse anlaşmayı sabitlemeden hemen önce güncelliği tekrar kontrol edin.",
        ],
      },
      {
        id: "call-check",
        icon: BookOpenCheck,
        title: "4. Tek aramada mutlaka netleştirin",
        note: "Gerçekler net değilken pazarlığa başlamayın. Önce sefer şartı, sonra fiyat.",
        bullets: [
          "Yük hâlâ açık mı / araç hâlâ boş mu?",
          "Nereden ve nereye: şehir, gerekiyorsa açık adres, tarih ve araç verme saat aralığı.",
          "Yük nedir, ağırlık, hacim, koli/adet; hangi kasa ve hangi yükleme/boşaltma şekli gerekir.",
          "Özel şart var mı: sıcaklık, parsiyel, üstten/yandan yükleme, rejim, sınır, gümrük, ambalaj/iade vb.",
          "Son fiyat, para birimi, KDV dahil/haric, kim kime öder, avans, kalan ödeme tarihi ve bekleme şartı.",
          "Yükleme/boşaltma sorumlusu ve fiyatı kim onaylıyor.",
          "Görüşmeden sonra şartları kısa yazılı mesajla özetleyip teyit alın. Bir şart değişirse eski teyide güvenmeyin.",
        ],
      },
      {
        id: "documents",
        icon: FileCheck2,
        title: "5. Belgeler: yalnızca bu sefer için gerekli olanı kontrol edin",
        note: "Amaç evrak sınavı yapmak değildir. Sürücü, araç, taşıyıcı ve yükün bu seferi gerçekten yapabildiğini doğrulamaktır.",
        bullets: [
          "Sürücü: kimlik/pasaport, gerekli sınıfta sürücü belgesi, çalışan telefon.",
          "Araç: araç/çekici ve römork tescil belgeleri, plakalar, geçerli sigorta ve söylenen kasa/kapasiteye uygunluk.",
          "Taşıyıcı: şirket/kişi adı, çalışan şirket bilgileri ve iletişim. Bilgiler anlaştığınız ve ödeme yapacağınız tarafla eşleşmelidir.",
          "Yurtiçi sefer: yükleyenin o taşıma için verdiği ticari ve taşıma belgeleri.",
          "Uluslararası sefer: gerektiğinde pasaport/giriş belgeleri, CMR ve o rota için gerekiyorsa geçiş izni/dozvol.",
          "TIR yalnızca sefer gerçekten TIR ile gidiyorsa kontrol edilir. ADR yalnızca tehlikeli yükte. Özel belge yalnızca yük veya rota gerektiriyorsa.",
          "Bir belge şüpheliyse tartışmayın ve parayı riske atmayın: araç çıkmadan işi DURAKLATIN ve yeniden doğrulayın.",
        ],
      },
      {
        id: "margin",
        icon: CircleDollarSign,
        title: "6. Pazarlık ve marj: araç çıkmadan sabitleyin",
        note: "Marj sefer sonunda tesadüfen oluşmaz. Araç yüklemeye çıkmadan önce net olmalıdır.",
        bullets: [
          "Müşteriyle onun nihai fiyatını ve ödeme şartlarını sabitleyin. Taşıyıcıyla ona ödenecek/alacağı tutarı ayrı sabitleyin.",
          "Müşteriye taşıyıcı fiyatının göründüğü mesaj/screenshot göndermeyin; taşıyıcıya da müşteri fiyatını göndermeyin. İki pazarlık ayrı kalmalıdır.",
          "Marj = müşteri fiyatı − taşıyıcı fiyatı − bu işe ait doğrudan masraflar.",
          "Anlaşmadan önce bekleme, ekstra durak, aktarma, ücretli yol/harç ve diğer olası masrafları kimin ödeyeceğini netleştirin. Aksi halde marjı yerler.",
          "Kim ödüyor, kime ödüyor, ne zaman, hangi para biriminde ve hangi hesaba — net değilse seferi onaylamayın.",
          "Anlaşmadan sonra her tarafa yalnızca kendi şartlarını tek kısa mesajla yazın ve teyit alın. Daha fazla marj için anlaşılan şartı sonradan değiştirmeyin.",
        ],
      },
      {
        id: "trip",
        icon: BookOpenCheck,
        title: "7. Sefer: araç sevkinden ödemeye kadar kontrol",
        bullets: [
          "Araç çıkmadan: sürücü, telefon, plaka, çıkış saati, ETA ve yükleme kontağı teyit edilmiş olmalı.",
          "Yüklemede: araç geldi; yük, ağırlık ve şartlar eşleşiyor; sürücü gerekli belgeleri aldı.",
          "Yolda: kritik noktaları ve gecikmeleri takip edin. Sorun varsa önce sürücüden gerçeği alın, sonra müşteriye yeni ETA/çözümle haber verin.",
          "Boşaltmada: teslim teyidini ve bu sefer için gereken imzalı belge/kabul kanıtını alın.",
          "Boşaltmadan sonra: hesapları kapatın, gerçek masrafı ve gerçek marjı kontrol edin. Açık belge, ihtilaf veya ödeme kalmadığında sefer kapanmıştır.",
        ],
      },
      {
        id: "stop",
        icon: ShieldAlert,
        title: "8. Ne zaman durup yeniden doğrulamalısınız",
        bullets: [
          "Anlaşmadan sonra fiyat, rota, ağırlık, kasa veya tarih aniden değişti.",
          "Ödeme öncesi alıcı, kart, hesap veya banka bilgisi değişti.",
          "Kişi şirketi, aracı, sürücüyü veya yükleme kontağını doğrulayamıyor.",
          "Ana bilgiler doğrulanmadan acil avans isteniyor.",
          "Araç boş deniyor ama sürücü konumunu veya yüklemeye yetişme saatini teyit etmiyor.",
          "Yük açık deniyor ama yüklemeyi kimse teyit etmiyor.",
          "Bu durumda tartışmayın ve tahmin yürütmeyin. Durum: DURAKLAT → yeniden doğrula → sonra devam et.",
        ],
      },
      {
        id: "terms",
        icon: BookOpenCheck,
        title: "9. Sadece gerekli terimler",
        bullets: [
          "Fiyat / navlun — taşıma için anlaşılan tutar.",
          "Marj — müşteri fiyatı ile taşıyıcı maliyeti arasında doğrudan giderlerden sonra kalan tutar.",
          "Taşıyıcı — taşımayı araç/sürücü ile fiilen yapan taraf.",
          "Nakliye organizatörü / ekspeditör — tarafları bulur, şartları koordine eder ve taşımayı takip eder.",
          "Parsiyel / ek yük — yük aracın sadece bir bölümünü kullanır; başka uyumlu yükler de taşınabilir.",
          "CMR — ilgili uluslararası karayolu seferinde kullanılan uluslararası taşıma senedi.",
          "Dozvol / geçiş izni — uluslararası rota gerektiriyorsa kullanılan izin.",
          "TIR — uluslararası gümrük transit rejimi; her seferde kullanılmaz.",
          "ADR — tehlikeli yük taşımacılığı şartları.",
          "Bekleme — yükleme/boşaltmada anlaşılan sürenin aşılması.",
          "ETA — tahmini varış zamanı.",
          "POD — teslim/tesellüm kanıtı.",
        ],
      },
    ],
    finishTitle: "Ana formül",
    finishText: "Akışı kaydırma → net arama yap → bildirimleri aç → güncelliği teyit et → araç ve belgeleri kontrol et → iki fiyatı sabitle → marjı koru → paraya ve belgelere kadar seferi takip et.",
  },
};

function normalizeLang(lang) {
  const raw = String(lang || "").toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

export function expeditorInstructionMeta(lang) {
  const safe = normalizeLang(lang);
  return {
    title: COPY[safe].settingsTitle,
    description: COPY[safe].shortDescription,
  };
}

function BulletList({ items }) {
  return (
    <ul className="rpi-bullets">
      {items.map((item, index) => (
        <li key={`${index}-${item.slice(0, 18)}`}>{item}</li>
      ))}
    </ul>
  );
}

export default function ReisorProExpeditorInstruction({
  lang = "ru",
  pushState,
  onEnableNotifications,
  onBack,
}) {
  const safeLang = normalizeLang(lang);
  const t = COPY[safeLang];
  const notificationReady = Boolean(pushState?.ok);

  return (
    <section className="rpi-page" data-reisor-expeditor-instruction="v2">
      <button type="button" className="rpi-back" onClick={onBack}>
        <ChevronLeft size={19} aria-hidden="true" />
        <span>{t.back}</span>
      </button>

      <header className="rpi-hero">
        <div className="rpi-hero__icon"><BookOpenCheck size={24} aria-hidden="true" /></div>
        <div>
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
        </div>
      </header>

      <div className="rpi-golden">
        <strong>{t.goldenTitle}</strong>
        <p>{t.goldenText}</p>
      </div>

      <div className="rpi-notify">
        <div className="rpi-notify__head">
          <Bell size={20} aria-hidden="true" />
          <strong>{t.notifyTitle}</strong>
        </div>
        <p>{t.notifyText}</p>
        <button
          type="button"
          className={notificationReady ? "is-ready" : ""}
          disabled={Boolean(pushState?.busy) || notificationReady}
          onClick={onEnableNotifications}
        >
          {pushState?.busy ? t.notifyBusy : notificationReady ? t.notifyDone : t.notifyButton}
        </button>
        {pushState?.message ? <small>{pushState.message}</small> : null}
      </div>

      <div className="rpi-flow">
        <div className="rpi-section-title">{t.flowTitle}</div>
        <ol>
          {t.flow.map((item, index) => <li key={item}><b>{index + 1}</b><span>{item}</span></li>)}
        </ol>
      </div>

      <div className="rpi-sections">
        {t.sections.map((section, index) => {
          const Icon = section.icon || BookOpenCheck;
          return (
            <details key={section.id} className="rpi-detail" open={index === 0}>
              <summary>
                <span className="rpi-detail__icon"><Icon size={19} aria-hidden="true" /></span>
                <span>{section.title}</span>
              </summary>
              <div className="rpi-detail__body">
                {section.note ? <p className="rpi-note">{section.note}</p> : null}
                <BulletList items={section.bullets} />
              </div>
            </details>
          );
        })}
      </div>

      <div className="rpi-finish">
        <strong>{t.finishTitle}</strong>
        <p>{t.finishText}</p>
      </div>
    </section>
  );
}
